/**
 * Normalizes a string into a URL-friendly slug.
 * - Converts to lowercase
 * - Removes diacritics/accents
 * - Replaces non-alphanumeric characters with hyphens
 * - Removes leading/trailing hyphens
 * - Collapses multiple consecutive hyphens
 *
 * @param input - The string to normalize
 * @returns The normalized slug, or empty string if input produces no valid characters
 */
export const normalizeSlug = (
  input: string,
  options?: { keepTrailingHyphen?: boolean }
): string => {
  let normalized = input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9-]+/g, '-') // Replace non-alphanumeric (excluding hyphens) with hyphens
    .replace(/-{2,}/g, '-') // Collapse multiple hyphens
    .replace(/^-+/g, ''); // Remove leading hyphens

  if (!options?.keepTrailingHyphen) {
    normalized = normalized.replace(/-+$/g, ''); // Remove trailing hyphens
  }

  return normalized;
};

/**
 * Validates if a slug is in the correct format.
 * Valid slugs are:
 * - 3-60 characters long
 * - Contain only lowercase letters, numbers, and hyphens
 * - Don't start or end with hyphens
 * - Don't have consecutive hyphens
 *
 * @param slug - The slug to validate
 * @returns Whether the slug is valid
 */
export const isValidSlugFormat = (slug: string): boolean => {
  if (slug.length < 3 || slug.length > 60) return false;
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
};
